import React from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { List, Popper } from '@xsolla/ui-core';
import { BlockCircle, More, Profile, Trash } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { unbanUser } from '../../../api';
import { BanOptions, User, UsersData } from '../../../api/users/types';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { BanUserModal } from '../UserSettingsForm/BanUserModal';
import DeleteUserModal from '../UserSettingsForm/DeleteUsersModal';
import { ListWrapper, UserSettingsWrapper } from './style';
import { RouteParams } from './types';

type Props = RouteComponentProps & {
  userId: string;
  externalId: string | null;
  registered: string;
  setUsersState: React.Dispatch<React.SetStateAction<UsersData>>;
  banData: BanOptions | null;
};

const SelectUserSettings = ({
  externalId,
  banData,
  registered,
  setUsersState,
  history,
  match,
  userId
}: Props): JSX.Element => {
  const { userSettingPath } = usePaths();
  const { projectId } = match.params as RouteParams;
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const toUserSettings = () => history.push({ pathname: userSettingPath(projectId, userId) });

  const [onOpenBanUserModal, onCloseBanUserModal] = useModal(
    () => (
      <BanUserModal
        onBan={(banDataResponse) => {
          setUsersState((prev: UsersData) => {
            return {
              ...prev,
              users: prev.users.map((user: User) => {
                if (user.id === userId) {
                  return {
                    ...user,
                    ban: banDataResponse
                  };
                }
                return user;
              })
            };
          });
        }}
        externalId={externalId}
        banData={banData}
        registered={registered}
        projectId={projectId}
        userId={userId}
        onClose={onCloseBanUserModal}
      />
    ),
    []
  );

  const [onOpenDeleteModal, onCloseDeleteModal] = useModal(
    () => (
      <DeleteUserModal
        userId={userId}
        projectId={projectId}
        onClose={onCloseDeleteModal}
        onDelete={() => {
          setUsersState((prev: UsersData) => {
            return {
              ...prev,
              users: prev.users.filter((user: User) => user.id !== userId)
            };
          });
          onCloseDeleteModal();
        }}
      />
    ),
    [userId]
  );

  const unbanHandle = async () => {
    try {
      await unbanUser(projectId, userId);
      setUsersState((prev: UsersData) => {
        return {
          ...prev,
          users: prev.users.map((user: User) => {
            if (user.id === userId) {
              return {
                ...user,
                ban: null
              };
            }
            return user;
          })
        };
      });
      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    }
  };

  return (
    <ListWrapper>
      <Popper
        placement="bottom-end"
        entry={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <List
            items={[
              {
                children: intl.messages['users.list-block.table.settings.view'],
                icon: Profile,
                onClick: toUserSettings,
                'data-test-id': 'user-profile'
              },
              {
                children:
                  intl.messages[
                    `users.list-block.table.settings.${banData?.date_from ? 'unban' : 'ban'}`
                  ],
                icon: banData?.date_from ? Trash : BlockCircle,
                palette: banData?.date_from ? null : 'danger',
                onClick: banData?.date_from ? unbanHandle : onOpenBanUserModal,
                'data-test-id': 'user-ban'
              },
              {
                children: intl.messages['users.list-block.table.settings.delete'],
                icon: Trash,
                palette: 'danger',
                onClick: onOpenDeleteModal,
                'data-test-id': 'user-delete'
              }
            ]}
          />
        }>
        {(referenceProps) => (
          <UserSettingsWrapper {...referenceProps}>
            <More data-test-id="user-more" />
          </UserSettingsWrapper>
        )}
      </Popper>
    </ListWrapper>
  );
};

export default withRouter(SelectUserSettings);
